import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress } from '@mui/material';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SectionCard from '../ui/SectionCard';
import { Row } from '../dashboard/Row';
import * as actions from '../../../../redux/actions';

// เซลล์เดียว label เล็กจางด้านบน + ค่าด้านล่าง (ต่างจาก Row ที่ label อยู่ซ้าย-ค่าอยู่ขวา) — ใช้
// เฉพาะฟิลด์ที่ต้องการโชว์แบบ "การ์ดข้อมูล" เด่นๆ (ชื่อลูกค้า/บริษัท, และคู่ฟิลด์ใน FieldGrid
// ด้านล่าง) ไม่มีกรอบของตัวเอง ให้ parent คุมกรอบ/เส้นแบ่งแทน
function FieldStack({ label, value, bold }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 500} sx={{ wordBreak: 'break-word' }}>
        {value ?? '-'}
      </Typography>
    </Box>
  );
}

// ตารางฟิลด์ 2 คอลัมน์ต่อแถว มีเส้นแบ่งบาง ๆ ระหว่างช่อง (ระหว่างคอลัมน์ + ระหว่างแถว) — ใช้กับ
// กลุ่มฟิลด์ที่อยากให้จับคู่กันเห็นชัด (เช่น ชื่อผู้ติดต่อ/เบอร์มือถือ) ต่าง จาก Row เดี่ยว ๆ ด้านล่าง
// fields.length ต้องเป็นเลขคู่เสมอ (คู่ต่อแถว) ไม่รองรับจำนวนคี่/full-width แทรกกลาง
function FieldGrid({ fields }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {fields.map((f, idx) => (
        <Box
          key={f.label}
          sx={{
            px: 2,
            py: 1.25,
            borderRight: idx % 2 === 0 ? '1px solid' : 'none',
            borderBottom: idx < fields.length - 2 ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <FieldStack label={f.label} value={f.value} />
        </Box>
      ))}
    </Box>
  );
}

// ดึงข้อมูลลูกค้าเต็ม (Contact model) ตรงจาก contact_id ของ opportunity มาโชว์ — fetch ตรงจาก
// dispatch(contactGet) แล้วเก็บ local state เอง (ผูกกับ contactId ของ fetch นั้นๆ ตรงๆ) ไม่พึ่ง
// state.contact (redux) ที่ใช้ร่วมกับ contactOptions ของ Autocomplete ใน OpportunityInfoCard
// (list, filter เฉพาะ contactType: 'client') กัน race/เขียนทับแบบเดียวกับบั๊ก state.project
// เดิมของโมดูล Project (ดู CLAUDE.md)
//
// เลย์เอาต์ใหม่ — แบ่งเป็น 4 การ์ดย่อยในกริด 2 คอลัมน์ (ตามภาพตัวอย่างที่ user ส่งมา): ข้อมูลหลัก,
// ช่องทางติดต่อ (จับคู่ label/value เป็นกริด 2 คอลัมน์มีเส้นแบ่ง), ช่องทางสื่อสาร/รายละเอียดธุรกิจ
// (แถวเดี่ยว label ซ้าย-ค่าขวา ใช้ Row เดิม) — ไม่ทำเป็น bilingual header (Contact Platforms/
// Business Details) ตามภาพต้นฉบับ เพราะที่เหลือทั้งโมดูลเป็นไทยล้วน ให้สม่ำเสมอกัน
export function ContactInfoCard({ contactId }) {
  const dispatch = useDispatch();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contactId) {
      setContact(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    dispatch(actions.contactGet(contactId))
      .then((data) => { if (!cancelled) setContact(data); })
      .catch(() => { if (!cancelled) setContact(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch, contactId]);

  const address = contact
    ? [
      contact.address1, contact.address2, contact.address3,
      contact.tambon, contact.amphoe, contact.province, contact.zipcode,
    ].filter(Boolean).join(' ')
    : '';

  return (
    <SectionCard icon={<ContactPageOutlinedIcon />} title="ข้อมูลลูกค้า" accentColor="warning.main">
      {!contactId ? (
        <Typography variant="body2" color="text.secondary">ยังไม่ได้เลือกลูกค้า</Typography>
      ) : loading || !contact ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          <SectionCard icon={<PersonOutlineOutlinedIcon />} title="ข้อมูลหลัก" accentColor="warning.main" dense>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FieldStack label="ชื่อลูกค้า/บริษัท" value={contact.name} bold />
              <FieldGrid
                fields={[
                  { label: 'เลขผู้เสียภาษี', value: contact.taxId },
                  { label: 'รหัสสาขา', value: contact.branch_code },
                ]}
              />
            </Box>
          </SectionCard>

          <SectionCard icon={<CallOutlinedIcon />} title="ช่องทางติดต่อ" accentColor="warning.main" dense>
            <FieldGrid
              fields={[
                { label: 'ชื่อผู้ติดต่อ', value: contact.contact_name },
                { label: 'เบอร์มือถือ', value: contact.mobile },
                { label: 'เบอร์สำนักงาน', value: contact.office_phone },
                { label: 'แฟกซ์', value: contact.fax },
              ]}
            />
          </SectionCard>

          <SectionCard icon={<ChatBubbleOutlineOutlinedIcon />} title="ช่องทางสื่อสาร" accentColor="warning.main" dense>
            <Box>
              <Row label="อีเมล" value={contact.email} />
              <Row label="Line ID" value={contact.line_id} />
              <Row label="เว็บไซต์" value={contact.website} />
            </Box>
          </SectionCard>

          <SectionCard icon={<CreditCardOutlinedIcon />} title="รายละเอียดธุรกิจ" accentColor="warning.main" dense>
            <Box>
              <Row label="สาขา" value={contact.office_branch} />
              <Row label="เครดิต (วัน)" value={contact.credit_days} />
              <Row label="ที่อยู่" value={address || null} />
            </Box>
          </SectionCard>
        </Box>
      )}
    </SectionCard>
  );
}

export default ContactInfoCard;
